import { useMemo } from 'react';
import type { Container } from '@/types/container';
import type { Waypoint } from '@/components/application/map/route-map';
import { formatShortDate } from '@/utils/date-formatters';

// Marker SVG generico per la nave
const ShipMarker = ({ color }: { color: string }) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M10 0C4.477 0 0 4.477 0 10C0 15.523 10 20 10 20C10 20 20 15.523 20 10C20 4.477 15.523 0 10 0Z"
            fill={color}
        />
        <circle cx="10" cy="10" r="4" fill="white" />
    </svg>
);

interface ContainerTimelineProps {
    container: Container;
    waypoints: Waypoint[];
}

type VesselState = 'in-transit' | 'waiting' | 'almost-arrived' | 'arrived';

export const ContainerTimeline = ({ container, waypoints }: ContainerTimelineProps) => {
    // Progress normalizzato 0–100 per evitare NaN/undefined in posizionamento marker
    const shipPosition = useMemo(
        () => Math.min(100, Math.max(0, Number(container.progress) ?? 0)),
        [container.progress]
    );

    // Determina lo stato del vessel (usa shipPosition per coerenza)
    const vesselState = useMemo((): VesselState => {
        const { status } = container;

        // Arrived at destination or delivered
        if (status === 'Delivered' || status === 'Arrived' || shipPosition === 100) {
            return 'arrived';
        }

        // In transit — position along route
        if (status === 'In Transit') {
            if (shipPosition >= 75) return 'almost-arrived';
            if (shipPosition > 0) return 'in-transit';
        }

        // Booked, Draft, On Hold, Cancelled — waiting at origin
        return 'waiting';
    }, [container.status, shipPosition]);

    // Colori in base allo stato
    const getStateColors = (state: VesselState) => {
        switch (state) {
            case 'in-transit':
                return {
                    lineColor: '#6938ef', // brand-600
                    circleColor: '#6938ef',
                    shipColor: '#6938ef',
                };
            case 'waiting':
                return {
                    lineColor: '#dc6803', // warning-600
                    circleColor: '#dc6803',
                    shipColor: '#dc6803',
                };
            case 'almost-arrived':
            case 'arrived':
                return {
                    lineColor: '#079485', // success-600
                    circleColor: '#079485',
                    shipColor: '#079485',
                };
        }
    };

    const colors = getStateColors(vesselState);

    // Waypoints per la visualizzazione: se vuoti, costruiamo start/end da route
    const effectiveWaypoints = useMemo((): Waypoint[] => {
        if (waypoints.length >= 2) return waypoints;
        const from = container.route?.from ?? '-';
        const to = container.route?.to ?? '-';
        return [
            { id: 'start', lat: 0, lng: 0, label: from, type: 'start', info: { portName: from } },
            { id: 'end', lat: 0, lng: 0, label: to, type: 'end', info: { portName: to } },
        ];
    }, [waypoints, container.route?.from, container.route?.to]);

    const displayWaypoints = useMemo(() => {
        const start = effectiveWaypoints.find(w => w.type === 'start') || effectiveWaypoints[0];
        const end = effectiveWaypoints.find(w => w.type === 'end') || effectiveWaypoints[effectiveWaypoints.length - 1];
        const intermediate = effectiveWaypoints
            .filter(w => w.type !== 'start' && w.type !== 'end' && w.info?.portName)
            .slice(0, 1); // Mostra max 1 waypoint intermedio

        return { start, end, intermediate };
    }, [effectiveWaypoints]);

    const { start, end, intermediate } = displayWaypoints;

    // Calcola date
    const startDate = start?.info?.ata
        ? new Date(start.info.ata)
        : container.createdAt;
    const endDate = end?.info?.eta
        ? new Date(end.info.eta)
        : container.eta;

    // Determina se mostrare ATD o ETD per il punto di partenza
    const startDateLabel = start?.info?.ata ? 'ATD' : 'ETD';
    const startDateValue = formatShortDate(startDate);

    // Determina quali segmenti sono completati basandosi sul progress (usa shipPosition già normalizzato)
    const totalSegments = 1 + intermediate.length + 1; // start + intermediate + end
    const segmentSize = 100 / (totalSegments - 1); // Dimensione di ogni segmento

    const isStartCompleted = vesselState === 'arrived' || shipPosition > 0;
    const isIntermediateCompleted = intermediate.length > 0 && (
        vesselState === 'arrived' || shipPosition >= segmentSize
    );
    const isEndCompleted = vesselState === 'arrived';

    return (
        <div className="w-full">
            {/* Timeline Container */}
            <div className="relative w-full mb-3">
                {/* Nomi porti sopra la timeline */}
                <div className="relative flex items-start justify-between mb-2">
                    {/* Start Port Name */}
                    <div className="flex flex-col items-start" style={{ flex: '0 0 auto', minWidth: '100px' }}>
                        <div className="text-sm font-medium text-primary text-center">
                            {start?.info?.portName || container.route.from}
                        </div>
                    </div>

                    {/* Intermediate Port Name */}
                    {intermediate.length > 0 && (
                        <div className="flex flex-col items-center flex-1 px-4">
                            <div className="text-sm font-medium text-primary text-center">
                                {intermediate[0].info?.portName}
                            </div>
                        </div>
                    )}

                    {/* End Port Name */}
                    <div className="flex flex-col items-end" style={{ flex: '0 0 auto', minWidth: '100px' }}>
                        <div className="text-sm font-medium text-primary text-center">
                            {end?.info?.portName || container.route.to}
                        </div>
                    </div>
                </div>

                {/* Timeline con dot all'inizio e alla fine */}
                <div className="relative w-full py-2">
                    {/* Linea orizzontale */}
                    <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 rounded-full">
                        {/* Per stato waiting: tutta la linea è tratteggiata grigia */}
                        {vesselState === 'waiting' ? (
                            <div className="absolute top-0 left-0 right-0 h-full border-t-2 border-dashed border-gray-300 rounded-full" />
                        ) : (
                            <>
                                {/* Segmento completato */}
                                <div
                                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: `${Math.min(shipPosition, 100)}%`,
                                        backgroundColor: colors.lineColor
                                    }}
                                />
                                {/* Segmento futuro (tratteggiato) */}
                                {vesselState !== 'arrived' && (
                                    <div
                                        className="absolute top-0 h-full border-t-2 border-dashed border-gray-300 rounded-full"
                                        style={{
                                            left: `${Math.min(shipPosition, 100)}%`,
                                            width: `${100 - Math.min(shipPosition, 100)}%`,
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>

                    {/* Dot all'inizio della timeline */}
                    <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full border-2"
                        style={{
                            ...(isStartCompleted ? {
                                backgroundColor: colors.circleColor,
                                borderColor: colors.circleColor,
                                width: '12px',
                                height: '12px'
                            } : {
                                backgroundColor: 'white',
                                borderColor: '#d1d5db',
                                width: '16px',
                                height: '16px'
                            })
                        }}
                    />

                    {/* Dot intermedio (se presente) */}
                    {intermediate.length > 0 && (
                        <div
                            className="absolute top-1/2 -translate-y-1/2 rounded-full border-2"
                            style={{
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                ...(isIntermediateCompleted ? {
                                    backgroundColor: colors.circleColor,
                                    borderColor: colors.circleColor,
                                    width: '12px',
                                    height: '12px'
                                } : {
                                    backgroundColor: 'white',
                                    borderColor: '#d1d5db',
                                    width: '16px',
                                    height: '16px'
                                })
                            }}
                        />
                    )}

                    {/* Dot alla fine della timeline */}
                    <div
                        className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full border-2"
                        style={{
                            ...(isEndCompleted ? {
                                backgroundColor: colors.circleColor,
                                borderColor: colors.circleColor,
                                width: '12px',
                                height: '12px'
                            } : {
                                backgroundColor: 'white',
                                borderColor: '#d1d5db',
                                width: '16px',
                                height: '16px'
                            })
                        }}
                    />

                    {/* Ship Marker - posizionata dinamicamente */}
                    {vesselState !== 'waiting' && vesselState !== 'arrived' && shipPosition > 0 && (
                        <div
                            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                            style={{
                                left: `${Math.min(Math.max(shipPosition, 2), 98)}%`
                            }}
                        >
                            <ShipMarker color={colors.shipColor} />
                        </div>
                    )}
                    {/* Ship Marker per stato waiting - mostra all'origine */}
                    {vesselState === 'waiting' && (
                        <div
                            className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                        >
                            <ShipMarker color={colors.shipColor} />
                        </div>
                    )}
                </div>

                {/* Date sotto la timeline */}
                <div className="relative flex items-start justify-between mt-2">
                    {/* Start Date */}
                    <div className="flex flex-col items-start" style={{ flex: '0 0 auto', minWidth: '100px' }}>
                        <div className="text-xs text-tertiary text-center">
                            {startDateLabel}: {startDateValue}
                        </div>
                    </div>

                    {/* Intermediate Date */}
                    {intermediate.length > 0 && intermediate[0].info?.eta && (
                        <div className="flex flex-col items-center flex-1 px-4">
                            <div className="text-xs text-tertiary text-center">
                                ETA: {formatShortDate(new Date(intermediate[0].info.eta))}
                            </div>
                        </div>
                    )}

                    {/* End Date */}
                    <div className="flex flex-col items-end" style={{ flex: '0 0 auto', minWidth: '100px' }}>
                        <div className="text-xs text-tertiary text-center">
                            ETA: {formatShortDate(endDate)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

